import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  CircularProgress, 
  Chip,
  Avatar,
  AvatarGroup,
  Divider,
  Snackbar,
  Alert,
  Fab
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { getPublicClubs, getUserClubs } from '../../services/clubService';
import TopBar from '../TopBar';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

function ClubsList() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [publicClubs, setPublicClubs] = useState([]);
  const [userClubs, setUserClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchClubs = async () => {
      try {
        setLoading(true);
        
        // 공개 모임 조회
        const publicClubsData = await getPublicClubs(20);
        setPublicClubs(publicClubsData);

        // 로그인한 사용자의 모임 조회
        if (currentUser) {
          const userClubsData = await getUserClubs(currentUser.uid);
          setUserClubs(userClubsData);
        }
      } catch (error) {
        console.error('Error fetching clubs:', error);
        setSnackbar({
          open: true,
          message: '모임 목록을 불러오는 중 오류가 발생했습니다.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchClubs();
  }, [currentUser]);

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public':
        return <PublicIcon fontSize="small" />;
      case 'private':
        return <LockIcon fontSize="small" />;
      case 'secret':
        return <VisibilityOffIcon fontSize="small" />;
      default:
        return <PublicIcon fontSize="small" />;
    }
  };

  const getVisibilityText = (visibility) => {
    switch (visibility) {
      case 'public':
        return '공개';
      case 'private':
        return '비공개';
      case 'secret':
        return '비밀';
      default:
        return '공개';
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case 'public':
        return '#4CAF50';
      case 'private':
        return '#FF9800';
      case 'secret':
        return '#9C27B0';
      default:
        return '#4CAF50';
    }
  };

  const handleCreateClub = () => {
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: '모임을 만들려면 로그인이 필요합니다.',
        severity: 'warning'
      });
      return;
    }
    navigate('/clubs/new');
  };

  const handleClubClick = (club) => {
    navigate(`/clubs/${club.id}`);
  };

  if (loading) {
    return (
      <Box sx={{ pb: 7, maxWidth: '600px', margin: '0 auto', width: '100%' }}>
        <TopBar title="독서 모임" showBack={false} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 7, maxWidth: '600px', margin: '0 auto', width: '100%' }}>
      <TopBar title="독서 모임" showBack={false} />
      
      {/* 내 모임 섹션 */}
      {currentUser && userClubs.length > 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
            내 모임 ({userClubs.length})
          </Typography>
          {userClubs.map((club) => (
            <Card
              key={club.id}
              sx={{
                mb: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => handleClubClick(club)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', flex: 1 }}>
                    {club.name}
                  </Typography>
                  <Chip
                    icon={getVisibilityIcon(club.visibility)}
                    label={getVisibilityText(club.visibility)}
                    size="small"
                    sx={{
                      bgcolor: getVisibilityColor(club.visibility),
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 24
                    }}
                  />
                </Box>
                
                <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.5 }}>
                  {club.description || '설명이 없습니다.'}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon sx={{ fontSize: 16, color: '#999' }} />
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {club.memberCount || 0}명
                    </Typography>
                  </Box>
                  
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    {club.createdAt?.toDate?.() ? 
                      club.createdAt.toDate().toLocaleDateString() : 
                      '최근 생성'
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* 구분선 */}
      {currentUser && userClubs.length > 0 && publicClubs.length > 0 && (
        <Divider sx={{ mx: 2, my: 1 }} />
      )}

      {/* 공개 모임 섹션 */}
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
          공개 모임 ({publicClubs.length})
        </Typography>
        
        {publicClubs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <GroupsIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
              아직 공개 모임이 없습니다
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              첫 번째 모임을 만들어보세요!
            </Typography>
          </Box>
        ) : (
          publicClubs.map((club) => (
            <Card
              key={club.id}
              sx={{
                mb: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => handleClubClick(club)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', flex: 1 }}>
                    {club.name}
                  </Typography>
                  <Chip
                    icon={getVisibilityIcon(club.visibility)}
                    label={getVisibilityText(club.visibility)}
                    size="small"
                    sx={{
                      bgcolor: getVisibilityColor(club.visibility),
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 24
                    }}
                  />
                </Box>
                
                <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.5 }}>
                  {club.description || '설명이 없습니다.'}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon sx={{ fontSize: 16, color: '#999' }} />
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {club.memberCount || 0}명
                    </Typography>
                  </Box>
                  
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    {club.createdAt?.toDate?.() ? 
                      club.createdAt.toDate().toLocaleDateString() : 
                      '최근 생성'
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* 모임 만들기 FAB */}
      <Fab
        color="primary"
        aria-label="모임 만들기"
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 'calc(50% - 300px + 16px)', // 600px 최대 width 기준으로 중앙 정렬
          bgcolor: '#FFD700',
          color: '#333',
          zIndex: 1200,
          '&:hover': {
            bgcolor: '#FFE55C',
            transform: 'scale(1.05)',
          },
          transition: 'all 0.2s ease-in-out',
          // 모바일에서는 화면 우측에 고정
          '@media (max-width: 600px)': {
            right: 16,
          },
        }}
        onClick={handleCreateClub}
      >
        <AddIcon />
      </Fab>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ClubsList;
